import getIn from '@alexaivars/get-in';
import createPromiseSaga from 'lib/createPromiseSaga';
import { buffers } from 'redux-saga';
import capitalize from 'voca/capitalize';
import normalizingFetch from 'lib/normalizingFetch';
import { all, take, actionChannel, select, put } from 'redux-saga/effects';
import {
  LOAD_MORE_MATERIALS,
  LOAD_MORE_PRESSROOMS,
  SEARCH_MATERIALS,
  SEARCH_PRESSROOMS,
  CREATE_ALERT,
  CREATE_ALERT_FAILURE,
  CREATE_ALERT_SUCCESS,
  closeCreateAlertOverlay,
} from './actions';
import { getLocale } from 'modules/user/reducers';
import { AUTH_TOKEN_ID } from 'modules/user/constants';
import {
  getSite,
  getSearchQueryString,
  getSearchQueryTopics,
  getSearchRegionQueryString,
} from 'modules/global/reducers';
import { showSuccessToast, showFailureToast } from 'modules/toasters/actions';

window.dataLayer = window.dataLayer || [];

const getFieldsName = (queryString, topics, regions) => {
  const query = { topics: queryString || topics, regions };
  return Object.keys(query)
    .filter(n => !!query[n].length)
    .map(capitalize)
    .join(' & ');
};

const trackSearchEvent = (queryString, topics, regions) => {
  const field = getFieldsName(queryString, topics, regions);
  window.dataLayer.push({
    event: 'searchEvent',
    searchType: 'Story search',
    field,
  });
};

const getSites = siteCode => {
  if (siteCode === 'com') {
    return;
  }

  return siteCode === 'dach' ? ['de', 'at', 'ch'] : [siteCode];
};

export function* watchSearchMaterials() {
  const channel = yield actionChannel(SEARCH_MATERIALS, buffers.none());

  while (true) {
    const action = yield take(channel);
    const siteCode = getIn(action, ['payload', 'site', 'code'], 'com');
    const sites = getSites(siteCode);
    const queryString = getIn(action, ['payload', 'query']);
    const topics = getIn(action, ['payload', 'topics'], []);
    const regions = getIn(action, ['payload', 'regions'], []);
    yield* createPromiseSaga(
      action,
      normalizingFetch,
      `${process.env.REACT_APP_API_SEARCH_MATERIALS}`,
      {
        detailedResponse: true,
        method: 'POST',
        body: {
          queryString,
          topics,
          regions,
          materialTypes: ['Pressrelease', 'News', 'BlogPost'],
          sites,
        },
      },
    )();
    trackSearchEvent(queryString, topics, regions);
  }
}

export function* watchSearchPressrooms() {
  const channel = yield actionChannel(SEARCH_PRESSROOMS, buffers.none());

  while (true) {
    const action = yield take(channel);
    const siteCode = getIn(action, ['payload', 'site', 'code'], 'com');
    const sites = getSites(siteCode);
    const queryString = getIn(action, ['payload', 'query']);
    const topics = getIn(action, ['payload', 'topics'], []);
    const regions = getIn(action, ['payload', 'regions'], []);
    yield* createPromiseSaga(
      action,
      normalizingFetch,
      `${process.env.REACT_APP_API_SEARCH_PRESSROOMS}`,
      {
        detailedResponse: true,
        method: 'POST',
        body: {
          queryString,
          topics,
          regions,
          sites,
        },
      },
    )();
  }
}

export function* watchLoadMoreMaterials() {
  const channel = yield actionChannel(LOAD_MORE_MATERIALS);

  while (true) {
    const action = yield take(channel);
    yield* createPromiseSaga(
      action,
      normalizingFetch,
      getIn(action, ['payload', 'link']),
      {
        detailedResponse: true,
        method: 'POST',
      },
    )();
  }
}

export function* watchLoadMorePressrooms() {
  const channel = yield actionChannel(LOAD_MORE_PRESSROOMS);

  while (true) {
    const action = yield take(channel);
    yield* createPromiseSaga(
      action,
      normalizingFetch,
      getIn(action, ['payload', 'link']),
      {
        detailedResponse: true,
        method: 'POST',
      },
    )();
  }
}

function* watchCreateAlertSuggestions() {
  const channel = yield actionChannel(CREATE_ALERT);
  while (true) {
    const action = yield take(channel);
    const locale = yield select(getLocale);
    const site = yield select(getSite);
    const sites = site.code === 'com' ? undefined : [site.code];

    let alertData = getIn(action, ['payload']);
    const queryString = yield select(getSearchQueryString);
    let topics = yield select(getSearchQueryTopics);
    topics = topics.split(' ').filter(n => n);
    let regions = yield select(getSearchRegionQueryString);
    regions = regions.split(' ').filter(n => n);

    alertData['name'] = alertData['name'].trim();
    alertData['search'] = {
      sites,
      locales: [locale],
      queryString,
      topics,
      regions,
      materialTypes: ['Pressrelease', 'News', 'BlogPost'],
    };

    yield* createPromiseSaga(
      action,
      normalizingFetch,
      process.env.REACT_APP_API_ALERTS,
      {
        detailedResponse: true,
        method: 'POST',
        body: alertData,
        headers: {
          authorization: localStorage.getItem(AUTH_TOKEN_ID),
        },
      },
    )();
  }
}

export function* watchCreateAlertSuggestionsResult(i18n) {
  const channel = yield actionChannel([
    CREATE_ALERT_SUCCESS,
    CREATE_ALERT_FAILURE,
  ]);
  while (true) {
    const action = yield take(channel);
    yield put(closeCreateAlertOverlay());
    if (action.type === CREATE_ALERT_SUCCESS) {
      yield put(showSuccessToast(i18n.t`overlays_create-alert_success`));
    } else {
      yield put(showFailureToast(i18n.t`overlays_create-alert_failure`));
    }
  }
}

export default function* sagas(i18n) {
  yield all([
    watchSearchMaterials(),
    watchSearchPressrooms(),
    watchLoadMoreMaterials(),
    watchLoadMorePressrooms(),
    watchCreateAlertSuggestions(),
    watchCreateAlertSuggestionsResult(i18n),
  ]);
}
